import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { bookingService } from '../../services/bookingService';
import { isReturnTrip } from '../../services/tripService';
import { useAuth } from '../../auth/AuthContext';
import { Booking } from '../../types';
import { getSeatDefinition } from '../../constants/seatLayout';
import { Link } from '../../router/Router';
import {
  Clock,
  Calendar,
  MapPin,
  User,
  Bus,
  Printer,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  Building2,
  Lock,
} from 'lucide-react';

export const DigitalTicket: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const { user } = useAuth();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    const loadBooking = async () => {
      setIsLoading(true);
      if (user?.uid) {
        const data = await bookingService.getBookingById(bookingId, user.uid);
        setBooking(data);
      }
      setIsLoading(false);
    };

    loadBooking();
  }, [bookingId, user?.uid]);

  const handleCopyCode = () => {
    if (booking?.bookingCode) {
      navigator.clipboard.writeText(booking.bookingCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2500);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="ticket-page-wrapper" dir="rtl">
        <div className="loading-state-card">
          <div className="loading-spinner"></div>
          <p>جاري استخراج بيانات التذكرة الرقمية المعتمدة...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="ticket-page-wrapper" dir="rtl">
        <div className="empty-state-card">
          <AlertCircle size={48} className="empty-state-icon" style={{ color: '#EF4444' }} />
          <h3>لم يتم العثور على التذكرة أو غير مصرح بالوصول</h3>
          <p>
            تأكد من أنك مسجل بالحساب الصحيح الذي تم الحجز به، أو أن معرف الحجز صحيح.
          </p>
          <div className="empty-state-actions">
            <Link to="/student/bookings" className="btn-primary">
              عرض سجل حجوزاتي
            </Link>
            <Link to="/student" className="btn-secondary">
              العودة للوحة الطالب
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const seatDef = getSeatDefinition(Number(booking.seatNumber));

  const isPaid = booking.paymentStatus === 'PAID';
  const isPendingVerification = booking.paymentStatus === 'PENDING_VERIFICATION';
  const isAwaitingBranch = booking.paymentStatus === 'AWAITING_BRANCH_PAYMENT';
  const isRejected = booking.paymentStatus === 'REJECTED';
  const isCash = (!booking.paymentMethod || booking.paymentMethod === 'CASH') && booking.paymentStatus === 'UNPAID';

  // Safe QR payload only generated with active boarding token when confirmed & paid / authorized
  const qrVerificationPayload = JSON.stringify({
    app: 'OTO_STUDENTS',
    ticketId: booking.ticketId || booking.id,
    bookingCode: booking.bookingCode,
    seat: booking.seatNumber,
    date: booking.tripDate,
    paymentMethod: booking.paymentMethod || 'INSTAPAY',
    paymentStatus: booking.paymentStatus,
    activeBoarding: isPaid || isCash,
    hash: booking.ticketSecurityHash || 'HASH_SECURE',
  });

  return (
    <div className="ticket-page-wrapper" dir="rtl">
      {/* Top Breadcrumb & Status Pill */}
      <div className="ticket-header-bar no-print">
        <div className="header-breadcrumbs">
          <Link to="/student" className="breadcrumb-link">
            لوحة الطالب
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to="/student/bookings" className="breadcrumb-link">
            حجوزاتي
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">التذكرة الرقمية</span>
        </div>

        {isPaid ? (
          <div className="ticket-success-pill">
            <CheckCircle2 size={16} />
            <span>تم اعتماد الدفع وتأكيد المقعد بنجاح! التذكرة مفعلة للصعود.</span>
          </div>
        ) : isPendingVerification ? (
          <div className="ticket-warning-pill pending-pill">
            <Clock size={16} />
            <span>طلب الحجز وإثبات التحويل قيد مراجعة وتدقيق الإدارة.</span>
          </div>
        ) : isRejected ? (
          <div className="ticket-danger-pill">
            <AlertCircle size={16} />
            <span>تعذر اعتماد إثبات التحويل السابق. يرجى إعادة إرسال الإثبات الصحيح.</span>
            <Link to={`/student/payment/${booking.id}`} className="btn-pay-now-inline">
              إعادة الرفع الآن
            </Link>
          </div>
        ) : isAwaitingBranch ? (
          <div className="ticket-info-pill">
            <Building2 size={16} />
            <span>في انتظار السداد بالفرع لتفعيل التذكرة.</span>
            <Link to={`/student/payment/${booking.id}`} className="btn-pay-now-inline">
              بيانات الفرع
            </Link>
          </div>
        ) : (
          <div className="ticket-success-pill">
            <CheckCircle2 size={16} />
            <span>حجزك مؤكد — سداد قيمة التذكرة نقدًا عند الصعود للسائق.</span>
          </div>
        )}
      </div>

      {/* Main Boarding Pass Card */}
      <div className="ticket-pass-container">
        {/* Ticket Header */}
        <div className="ticket-pass-header">
          <div className="ticket-brand">
            <span className="brand-logo-pill" style={{ padding: '0', overflow: 'hidden', width: '36px', height: '36px', borderRadius: '10px' }}>
              <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </span>
            <div>
              <h2 className="brand-name">OTO STUDENTS</h2>
              <span className="brand-slogan">تذكرة سفر جامعية معتمدة</span>
            </div>
          </div>

          <div className="ticket-badges-group">
            {isPaid ? (
              <div className="ticket-badge-paid">
                <CheckCircle2 size={15} />
                <span>مدفوع ومعتمد (PAID)</span>
              </div>
            ) : isPendingVerification ? (
              <div className="ticket-badge-pending">
                <Clock size={15} />
                <span>قيد مراجعة الدفع</span>
              </div>
            ) : isRejected ? (
              <div className="ticket-badge-danger">
                <AlertCircle size={15} />
                <span>إثبات مرفوض</span>
              </div>
            ) : isAwaitingBranch ? (
              <div className="ticket-badge-branch">
                <Building2 size={15} />
                <span>الدفع بالفرع</span>
              </div>
            ) : (
              <div className="ticket-badge-cash">
                <span>الدفع عند الركوب (CASH)</span>
              </div>
            )}
          </div>
        </div>

        {/* Ticket Body: Two Column (Info & QR) */}
        <div className="ticket-pass-body">
          {/* Left Column: QR Code & Verification */}
          <div className="ticket-qr-section">
            <div className="qr-code-box">
              {isPaid || isCash ? (
                <QRCodeSVG
                  value={qrVerificationPayload}
                  size={160}
                  level="M"
                  includeMargin={true}
                />
              ) : (
                <div className="qr-disabled-placeholder">
                  <Lock size={36} className="lock-icon" />
                  <span>رمز الصعود الرقمي (QR)</span>
                  <p>يتم تفعيل الرمز تلقائيًا فور اعتماد السداد من الإدارة</p>
                </div>
              )}
            </div>

            <div className="qr-instructions">
              <p className="qr-code-label">رمز التحقق الرقمي (Boarding Verification)</p>
              <p className="qr-code-hint">
                {isPaid || isCash
                  ? 'يتم مسح هذا الرمز بواسطة سائق السيارة أو مشرف المحطة لتأكيد الصعود.'
                  : 'التذكرة قيد المراجعة الإدارية، ولا تعتبر صالحة للصعود حتى يتم اعتماد السداد.'}
              </p>
            </div>

            <div className="booking-ref-box">
              <span className="ref-label">كود الحجز:</span>
              <div className="ref-code-row">
                <code className="ref-code">{booking.bookingCode}</code>
                <button
                  className="copy-ref-btn no-print"
                  onClick={handleCopyCode}
                  title="نسخ كود الحجز"
                >
                  {copiedCode ? <Check size={14} color="#10B981" /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Travel Details */}
          <div className="ticket-details-section">
            {/* Direction & Group Header if present */}
            {(booking.direction || booking.bookingGroupCode) && (
              <div className="ticket-row-group" style={{ background: '#F8FAFC', padding: '0.6rem 0.85rem', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                {booking.direction && (
                  <div className="detail-cell">
                    <span className="cell-label">مسار الرحلة:</span>
                    <span className="cell-val bold" style={{ color: isReturnTrip(booking.direction) ? '#7C3AED' : '#0284C7' }}>
                      {isReturnTrip(booking.direction) ? '🔄 رحلة عودة (إلى الإسكندرية)' : '➡️ رحلة ذهاب (إلى الجامعة)'}
                    </span>
                  </div>
                )}
                {booking.bookingGroupCode && (
                  <div className="detail-cell">
                    <span className="cell-label">مجموعة الحجز:</span>
                    <span className="cell-val bold" style={{ fontFamily: 'monospace', color: '#0F172A' }}>
                      {booking.bookingGroupCode}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Passenger & University */}
            <div className="ticket-row-group">
              <div className="detail-cell">
                <span className="cell-label">
                  <User size={13} /> اسم الطالب المسافر:
                </span>
                <span className="cell-val bold">{booking.studentSnapshot.fullName}</span>
              </div>

              <div className="detail-cell">
                <span className="cell-label">الجامعة التابع لها:</span>
                <span className="cell-val bold">{booking.universityName}</span>
              </div>
            </div>

            {/* Departure Station & Destination */}
            <div className="ticket-row-group">
              <div className="detail-cell">
                <span className="cell-label">
                  <MapPin size={13} /> محطة الانطلاق:
                </span>
                <span className="cell-val">
                  {booking.origin || (isReturnTrip(booking.direction) ? booking.universityName : 'موقف سيدي جابر / محطة مصر')}
                </span>
              </div>

              <div className="detail-cell">
                <span className="cell-label">
                  <MapPin size={13} /> محطة الوصول:
                </span>
                <span className="cell-val bold">
                  {booking.destination || (isReturnTrip(booking.direction) ? 'الإسكندرية (سيدي جابر / محطة مصر)' : booking.universityName)}
                </span>
              </div>
            </div>

            {/* Departure Time & Travel Date */}
            <div className="ticket-row-group">
              <div className="detail-cell">
                <span className="cell-label">
                  <Calendar size={13} /> تاريخ الرحلة:
                </span>
                <span className="cell-val bold">{booking.tripDate}</span>
              </div>

              <div className="detail-cell">
                <span className="cell-label">
                  <Clock size={13} /> موعد التحرك:
                </span>
                <span className="cell-val highlight">{booking.departureTime}</span>
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="ticket-row-group">
              <div className="detail-cell">
                <span className="cell-label">
                  <Bus size={13} /> لوحة ونوع المركبة:
                </span>
                <span className="cell-val">{booking.vehiclePlate || 'س ق د 1842'} (Toyota HiAce VIP)</span>
              </div>
              <div className="detail-cell">
                <span className="cell-label">رقم التذكرة (Ticket ID):</span>
                <span className="cell-val bold" style={{ fontFamily: 'monospace' }}>
                  {booking.ticketId || booking.id.substring(0, 10).toUpperCase()}
                </span>
              </div>
            </div>

            {/* Seat & Price Highlights */}
            <div className="ticket-highlight-box">
              <div className="highlight-cell seat-cell">
                <span className="hl-label">المقعد المحجوز:</span>
                <span className="hl-val">المقعد {booking.seatNumber}</span>
                <span className="hl-sub">{seatDef?.label || 'مقعد ركاب مريح'}</span>
              </div>

              <div className="highlight-cell price-cell">
                <span className="hl-label">القيمة الإجمالية:</span>
                <span className="hl-val price">{booking.price} ج.م</span>
                <span className="hl-sub">
                  {isPaid
                    ? 'تم السداد واعتماده رسميًا ✅'
                    : isPendingVerification
                    ? 'إثبات التحويل قيد مراجعة الإدارة ⏳'
                    : isRejected
                    ? 'إثبات التحويل مرفوض - يرجى إعادة الرفع'
                    : isAwaitingBranch
                    ? 'السداد نقدًا في الفرع'
                    : 'الدفع نقدًا عند الصعود للأتوبيس'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Footer Tear-Off Edge */}
        <div className="ticket-pass-footer">
          <p className="ticket-footer-note">
            ⚠️ يرجى التواجد في محطة الركوب قبل موعد التحرك بـ 15 دقيقة على الأقل لتأكيد ركوب الحافلة (50 راكباً).
          </p>
        </div>
      </div>

      {/* Action Buttons (Hidden on Print) */}
      <div className="ticket-actions-bar no-print">
        {isRejected && (
          <Link to={`/student/payment/${booking.id}`} className="btn-primary btn-pay-now">
            <ArrowRight size={16} />
            إعادة إرسال إثبات التحويل الآن
          </Link>
        )}

        {isAwaitingBranch && (
          <Link to={`/student/payment/${booking.id}`} className="btn-primary btn-pay-now">
            <Building2 size={16} />
            عرض تفاصيل السداد بالفرع
          </Link>
        )}

        {(isPaid || isCash) && (
          <button className="btn-secondary btn-print-ticket" onClick={handlePrint}>
            <Printer size={16} />
            <span>طباعة / حفظ التذكرة PDF</span>
          </button>
        )}

        <Link to="/student/bookings" className="btn-secondary">
          عرض جميع حجوزاتي
        </Link>

        <Link to="/student" className="btn-primary">
          <span>العودة للوحة الرئيسية</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};
